/**
 * Card Matcher for Scanner
 *
 * Uses Fuse.js for fuzzy matching OCR results against the card database.
 * Loads card data from the local JSON files in /public/cards/
 */

import Fuse from 'fuse.js';

export interface CardMatch {
  cardId: string;
  name: string;
  setId: string;
  setName: string;
  number: string;
  imageUrl?: string;
  score: number;
}

interface CardData {
  id: string;
  name: string;
  number?: string;
  set: {
    id: string;
    name: string;
  };
  images?: {
    small?: string;
    large?: string;
  };
}

interface SetInfo {
  id: string;
  name: string;
  series: string;
  releaseDate: string;
  total: number;
}

// Cache for loaded card data
let cardIndex: Fuse<CardData> | null = null;
let allCards: CardData[] = [];
let allSets: SetInfo[] = [];
let isLoading = false;
let loadPromise: Promise<void> | null = null;

/**
 * Load all sets info
 */
async function loadSets(): Promise<SetInfo[]> {
  try {
    const response = await fetch('/cards/sets.json');
    if (!response.ok) throw new Error('Failed to load sets');
    const data = await response.json();
    return data.sets || data;
  } catch (error) {
    console.error('Failed to load sets:', error);
    return [];
  }
}

/**
 * Load cards from a specific set
 */
async function loadSetCards(setId: string): Promise<CardData[]> {
  try {
    const response = await fetch(`/cards/${setId}.json`);
    if (!response.ok) return [];
    const data = await response.json();
    return (data.cards || data).map((card: any) => ({
      ...card,
      set: { id: setId, name: data.name || setId }
    }));
  } catch {
    return [];
  }
}

/**
 * Initialize the card matcher with all available cards
 */
export async function initMatcher(): Promise<void> {
  if (cardIndex) return;
  if (isLoading && loadPromise) {
    await loadPromise;
    return;
  }

  isLoading = true;
  loadPromise = (async () => {
    try {
      // Load sets first
      allSets = await loadSets();

      // Load cards from each set (limit for performance)
      const recentSets = allSets
        .sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime())
        .slice(0, 50); // Load only recent 50 sets for speed

      const cardPromises = recentSets.map(set => loadSetCards(set.id));
      const cardArrays = await Promise.all(cardPromises);
      allCards = cardArrays.flat();

      // Create Fuse index
      cardIndex = new Fuse(allCards, {
        keys: [
          { name: 'name', weight: 0.7 },
          { name: 'number', weight: 0.2 },
          { name: 'set.name', weight: 0.1 },
        ],
        includeScore: true,
        threshold: 0.4,
        ignoreLocation: true,
        useExtendedSearch: true,
      });

      console.log(`Card matcher initialized with ${allCards.length} cards from ${recentSets.length} sets`);
    } catch (error) {
      console.error('Failed to initialize matcher:', error);
    } finally {
      isLoading = false;
    }
  })();

  await loadPromise;
}

/**
 * Find best matches for a card name
 */
export async function findMatches(
  name: string,
  options?: {
    setCode?: string;
    cardNumber?: string;
    limit?: number;
  }
): Promise<CardMatch[]> {
  await initMatcher();

  if (!cardIndex || allCards.length === 0) {
    return [];
  }

  const limit = options?.limit || 5;

  // Build search query
  let results = cardIndex.search(name);

  // Filter by set code if provided
  if (options?.setCode) {
    const setCodeLower = options.setCode.toLowerCase();
    results = results.filter(r =>
      r.item.set.id.toLowerCase().includes(setCodeLower)
    );
  }

  // Filter by card number if provided
  if (options?.cardNumber) {
    results = results.filter(r =>
      r.item.number === options.cardNumber
    );
  }

  return results.slice(0, limit).map(result => ({
    cardId: result.item.id,
    name: result.item.name,
    setId: result.item.set.id,
    setName: result.item.set.name,
    number: result.item.number || '',
    imageUrl: result.item.images?.small,
    score: 1 - (result.score || 0), // Convert to 0-1 where 1 is best
  }));
}

/**
 * Find exact match by set code and card number
 */
export async function findBySetNumber(
  setCode: string,
  cardNumber: string
): Promise<CardMatch | null> {
  await initMatcher();

  const card = allCards.find(c =>
    c.set.id.toLowerCase() === setCode.toLowerCase() &&
    c.number === cardNumber
  );

  if (!card) return null;

  return {
    cardId: card.id,
    name: card.name,
    setId: card.set.id,
    setName: card.set.name,
    number: card.number || '',
    imageUrl: card.images?.small,
    score: 1,
  };
}

/**
 * Find card by ID
 */
export async function findById(cardId: string): Promise<CardMatch | null> {
  await initMatcher();

  const card = allCards.find(c => c.id === cardId);
  if (!card) return null;

  return {
    cardId: card.id,
    name: card.name,
    setId: card.set.id,
    setName: card.set.name,
    number: card.number || '',
    imageUrl: card.images?.small,
    score: 1,
  };
}

/**
 * Get best match from OCR results
 */
export async function matchFromOCR(
  ocrResult: {
    name: string;
    setNumber?: string;
    setCode?: string;
    cardNumber?: string;
    confidence: number;
  }
): Promise<{
  matches: CardMatch[];
  bestMatch: CardMatch | null;
  confidence: number;
}> {
  // If we have set code and card number, try exact match first
  if (ocrResult.setCode && ocrResult.cardNumber) {
    const exactMatch = await findBySetNumber(ocrResult.setCode, ocrResult.cardNumber);
    if (exactMatch) {
      return {
        matches: [exactMatch],
        bestMatch: exactMatch,
        confidence: Math.min(ocrResult.confidence, 95),
      };
    }
  }

  // Fall back to fuzzy name matching
  const matches = await findMatches(ocrResult.name, {
    setCode: ocrResult.setCode,
    cardNumber: ocrResult.cardNumber,
  });

  const bestMatch = matches[0] || null;

  // Adjust confidence based on match score
  const matchConfidence = bestMatch
    ? ocrResult.confidence * bestMatch.score
    : 0;

  return {
    matches,
    bestMatch,
    confidence: matchConfidence,
  };
}

/**
 * Get number of loaded cards
 */
export function getLoadedCardCount(): number {
  return allCards.length;
}

/**
 * Clear the matcher cache
 */
export function clearMatcher(): void {
  cardIndex = null;
  allCards = [];
  allSets = [];
  isLoading = false;
  loadPromise = null;
}
